import { useState } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  GripVertical,
  Check,
  X,
  AlertCircle,
  Save,
  ChevronDown,
  ChevronUp,
  Copy,
} from 'lucide-react';
import { Button, Card, Spinner, ConfirmDialog } from '@centraoneplus/ui';
import { pipelineService } from '@/services/pipeline.service';
import type {
  Pipeline,
  FieldDefinition,
  FieldType,
  FieldOption,
  FieldValidation,
  FieldGroup,
  PipelineFieldSchema,
} from '@/types/pipeline';
import { ThemedSelect } from '@components/common/ThemedSelect';

// Available field types
const FIELD_TYPES: { value: FieldType; label: string; description: string }[] = [
  { value: 'text', label: 'Text', description: 'Single line text input' },
  { value: 'textarea', label: 'Text Area', description: 'Multi-line text input' },
  { value: 'number', label: 'Number', description: 'Numeric input' },
  { value: 'email', label: 'Email', description: 'Email address' },
  { value: 'url', label: 'URL', description: 'Web address' },
  { value: 'phone', label: 'Phone', description: 'Phone number' },
  { value: 'date', label: 'Date', description: 'Date picker' },
  { value: 'datetime', label: 'Date & Time', description: 'Date and time picker' },
  { value: 'select', label: 'Dropdown', description: 'Single selection from options' },
  { value: 'multiselect', label: 'Multi-Select', description: 'Multiple selections' },
  { value: 'checkbox', label: 'Checkbox', description: 'Boolean toggle' },
  { value: 'radio', label: 'Radio', description: 'Single choice from options' },
  { value: 'currency', label: 'Currency', description: 'Monetary value' },
];

interface FieldFormData {
  key: string;
  name: string;
  type: FieldType;
  description: string;
  placeholder: string;
  defaultValue: string;
  options: FieldOption[];
  validation: FieldValidation;
  group: string;
}

const defaultFieldForm: FieldFormData = {
  key: '',
  name: '',
  type: 'text',
  description: '',
  placeholder: '',
  defaultValue: '',
  options: [],
  validation: {},
  group: '',
};

interface FieldsTabProps {
  pipeline: Pipeline;
  onUpdate: (updatedPipeline: Pipeline) => void;
}

export default function FieldsTab({ pipeline, onUpdate }: FieldsTabProps) {
  // Ensure fieldSchema and its nested arrays always have default values
  const fieldSchema: PipelineFieldSchema = {
    version: pipeline.fieldSchema?.version || '1.0.0',
    fields: pipeline.fieldSchema?.fields || [],
    fieldGroups: pipeline.fieldSchema?.fieldGroups || [],
  };
  const [isCreating, setIsCreating] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [fieldForm, setFieldForm] = useState<FieldFormData>(defaultFieldForm);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState<string | null>(null);

  const generateKey = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
  };

  const handleAddField = () => {
    if (!fieldForm.name.trim() || !fieldForm.key.trim()) return;

    // Check for duplicate keys
    if (fieldSchema.fields.some((f) => f.key === fieldForm.key)) {
      setError(`Field with key "${fieldForm.key}" already exists`);
      return;
    }

    const newField: FieldDefinition = {
      key: fieldForm.key,
      name: fieldForm.name,
      type: fieldForm.type,
      description: fieldForm.description || undefined,
      placeholder: fieldForm.placeholder || undefined,
      defaultValue: fieldForm.defaultValue || undefined,
      options: fieldForm.options.length > 0 ? fieldForm.options : undefined,
      validation: Object.keys(fieldForm.validation).length > 0 ? fieldForm.validation : undefined,
      group: fieldForm.group || undefined,
    };

    const updatedSchema: PipelineFieldSchema = {
      ...fieldSchema,
      fields: [...fieldSchema.fields, newField],
    };

    onUpdate({ ...pipeline, fieldSchema: updatedSchema });
    setIsCreating(false);
    setFieldForm(defaultFieldForm);
    setHasChanges(true);
    setError(null);
  };

  const handleUpdateField = (key: string) => {
    if (!fieldForm.name.trim()) return;

    const updatedField: FieldDefinition = {
      key: fieldForm.key,
      name: fieldForm.name,
      type: fieldForm.type,
      description: fieldForm.description || undefined,
      placeholder: fieldForm.placeholder || undefined,
      defaultValue: fieldForm.defaultValue || undefined,
      options: fieldForm.options.length > 0 ? fieldForm.options : undefined,
      validation: Object.keys(fieldForm.validation).length > 0 ? fieldForm.validation : undefined,
      group: fieldForm.group || undefined,
    };

    const updatedSchema: PipelineFieldSchema = {
      ...fieldSchema,
      fields: fieldSchema.fields.map((f) => (f.key === key ? updatedField : f)),
    };

    onUpdate({ ...pipeline, fieldSchema: updatedSchema });
    setEditingKey(null);
    setFieldForm(defaultFieldForm);
    setHasChanges(true);
  };

  const handleDeleteField = (key: string) => setFieldToDelete(key);

  const performDeleteField = () => {
    if (!fieldToDelete) return;
    const updatedSchema: PipelineFieldSchema = {
      ...fieldSchema,
      fields: fieldSchema.fields.filter((f) => f.key !== fieldToDelete),
    };
    onUpdate({ ...pipeline, fieldSchema: updatedSchema });
    setHasChanges(true);
    setFieldToDelete(null);
  };

  const handleSaveSchema = async () => {
    try {
      setIsSaving(true);
      setError(null);
      await pipelineService.updateFieldSchema(pipeline.id, fieldSchema);
      setHasChanges(false);
    } catch (err) {
      console.error('Error saving field schema:', err);
      setError('Failed to save field schema');
    } finally {
      setIsSaving(false);
    }
  };

  const startEdit = (field: FieldDefinition) => {
    setEditingKey(field.key);
    setFieldForm({
      key: field.key,
      name: field.name,
      type: field.type,
      description: field.description || '',
      placeholder: field.placeholder || '',
      defaultValue: String(field.defaultValue || ''),
      options: field.options || [],
      validation: field.validation || {},
      group: field.group || '',
    });
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setIsCreating(false);
    setFieldForm(defaultFieldForm);
  };

  const needsOptions = (type: FieldType): boolean => {
    return ['select', 'multiselect', 'radio'].includes(type);
  };

  return (
    <>
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Custom Fields</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Define custom fields for tickets in this pipeline
          </p>
        </div>
        <div className="flex gap-2">
          {hasChanges && (
            <Button onClick={handleSaveSchema} disabled={isSaving}>
              {isSaving ? <Spinner size="sm" /> : <Save className="h-4 w-4 mr-2" />}
              Save Schema
            </Button>
          )}
          <Button onClick={() => setIsCreating(true)} disabled={isCreating} variant="outline">
            <Plus className="h-4 w-4 mr-2" />
            Add Field
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Create Field Form */}
      {isCreating && (
        <Card className="p-4 border-2 border-primary-200 bg-primary-50">
          <h4 className="text-sm font-medium text-gray-900 mb-4 dark:text-gray-100">Add New Field</h4>
          <FieldForm
            form={fieldForm}
            onChange={setFieldForm}
            onSave={handleAddField}
            onCancel={cancelEdit}
            generateKey={generateKey}
            needsOptions={needsOptions}
          />
        </Card>
      )}

      {/* Fields List */}
      <div className="space-y-2">
        {fieldSchema.fields.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-gray-500 mb-4 dark:text-gray-400">No custom fields defined yet</p>
            <Button onClick={() => setIsCreating(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add your first field
            </Button>
          </Card>
        ) : (
          fieldSchema.fields.map((field) => (
            <Card key={field.key} className="overflow-hidden">
              {editingKey === field.key ? (
                <div className="p-4">
                  <FieldForm
                    form={fieldForm}
                    onChange={setFieldForm}
                    onSave={() => handleUpdateField(field.key)}
                    onCancel={cancelEdit}
                    generateKey={generateKey}
                    needsOptions={needsOptions}
                    isEditing
                  />
                </div>
              ) : (
                <>
                  <div
                    className="p-4 flex items-center gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    onClick={() => setExpandedKey(expandedKey === field.key ? null : field.key)}
                  >
                    <GripVertical className="h-4 w-4 text-gray-400 cursor-grab dark:text-gray-500" />

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-gray-100">{field.name}</span>
                        <code className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded dark:bg-gray-800 dark:text-gray-400">
                          {field.key}
                        </code>
                        <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
                          {FIELD_TYPES.find((t) => t.value === field.type)?.label || field.type}
                        </span>
                        {field.validation?.required && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">
                            Required
                          </span>
                        )}
                      </div>
                      {field.description && (
                        <p className="text-sm text-gray-500 truncate mt-1 dark:text-gray-400">{field.description}</p>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          startEdit(field);
                        }}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteField(field.key);
                        }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      {expandedKey === field.key ? (
                        <ChevronUp className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedKey === field.key && (
                    <div className="border-t border-gray-200 bg-gray-50 p-4 dark:bg-gray-900 dark:border-gray-700">
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Placeholder:</span>
                          <p className="text-gray-900 dark:text-gray-100">{field.placeholder || '-'}</p>
                        </div>
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Default Value:</span>
                          <p className="text-gray-900 dark:text-gray-100">{String(field.defaultValue) || '-'}</p>
                        </div>
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Group:</span>
                          <p className="text-gray-900 dark:text-gray-100">{field.group || '-'}</p>
                        </div>
                        {field.options && field.options.length > 0 && (
                          <div className="col-span-3">
                            <span className="text-gray-500 dark:text-gray-400">Options:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {field.options.map((opt) => (
                                <span
                                  key={opt.value}
                                  className="px-2 py-0.5 text-xs bg-gray-200 text-gray-700 rounded dark:bg-gray-700 dark:text-gray-200"
                                  style={opt.color ? { backgroundColor: opt.color + '20', color: opt.color } : undefined}
                                >
                                  {opt.label}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {field.validation && Object.keys(field.validation).length > 0 && (
                          <div className="col-span-3">
                            <span className="text-gray-500 dark:text-gray-400">Validation:</span>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {field.validation.minLength && (
                                <span className="text-xs">Min length: {field.validation.minLength}</span>
                              )}
                              {field.validation.maxLength && (
                                <span className="text-xs">Max length: {field.validation.maxLength}</span>
                              )}
                              {field.validation.min !== undefined && (
                                <span className="text-xs">Min: {field.validation.min}</span>
                              )}
                              {field.validation.max !== undefined && (
                                <span className="text-xs">Max: {field.validation.max}</span>
                              )}
                              {field.validation.pattern && (
                                <span className="text-xs">Pattern: {field.validation.pattern}</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </Card>
          ))
        )}
      </div>
    </div>
    <ConfirmDialog
      isOpen={fieldToDelete !== null}
      onClose={() => setFieldToDelete(null)}
      onConfirm={performDeleteField}
      title="Delete field"
      message="Are you sure you want to delete this field?"
      confirmLabel="Delete"
      cancelLabel="Cancel"
      variant="danger"
    />
    </>
  );
}

// Field Form Component
interface FieldFormProps {
  form: FieldFormData;
  onChange: (form: FieldFormData) => void;
  onSave: () => void;
  onCancel: () => void;
  generateKey: (name: string) => string;
  needsOptions: (type: FieldType) => boolean;
  isEditing?: boolean;
}

function FieldForm({ form, onChange, onSave, onCancel, generateKey, needsOptions, isEditing }: FieldFormProps) {
  const [newOption, setNewOption] = useState({ label: '', value: '' });

  const handleNameChange = (name: string) => {
    const updates: Partial<FieldFormData> = { name };
    if (!isEditing) {
      updates.key = generateKey(name);
    }
    onChange({ ...form, ...updates });
  };

  const addOption = () => {
    if (!newOption.label.trim()) return;
    const value = newOption.value.trim() || generateKey(newOption.label);
    onChange({
      ...form,
      options: [...form.options, { label: newOption.label, value }],
    });
    setNewOption({ label: '', value: '' });
  };

  const removeOption = (index: number) => {
    onChange({
      ...form,
      options: form.options.filter((_, i) => i !== index),
    });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">Field Name *</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="e.g., Order Number"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">Field Key *</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={form.key}
              onChange={(e) => onChange({ ...form, key: e.target.value })}
              placeholder="order_number"
              disabled={isEditing}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
            />
            {!isEditing && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onChange({ ...form, key: generateKey(form.name) })}
                title="Generate from name"
              >
                <Copy className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">Type *</label>
          <ThemedSelect
            value={form.type}
            onChange={(e) => onChange({ ...form, type: e.target.value as FieldType })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
          >
            {FIELD_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </ThemedSelect>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">Placeholder</label>
          <input
            type="text"
            value={form.placeholder}
            onChange={(e) => onChange({ ...form, placeholder: e.target.value })}
            placeholder="Enter placeholder text..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">Description</label>
        <input
          type="text"
          value={form.description}
          onChange={(e) => onChange({ ...form, description: e.target.value })}
          placeholder="Help text for this field..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
        />
      </div>

      {/* Options (for select, multiselect, radio) */}
      {needsOptions(form.type) && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">Options</label>
          <div className="space-y-2">
            {form.options.map((opt, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  value={opt.label}
                  readOnly
                  className="flex-1 px-2 py-1 text-sm border border-gray-200 rounded bg-gray-50 dark:bg-gray-900 dark:border-gray-700"
                />
                <code className="text-xs text-gray-500 dark:text-gray-400">{opt.value}</code>
                <Button variant="ghost" size="sm" onClick={() => removeOption(index)}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newOption.label}
                onChange={(e) => setNewOption({ ...newOption, label: e.target.value })}
                placeholder="Option label"
                className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                onKeyDown={(e) => e.key === 'Enter' && addOption()}
              />
              <Button variant="outline" size="sm" onClick={addOption}>
                <Plus className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Validation */}
      <div className="border-t pt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-200">Validation</label>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.validation.required || false}
              onChange={(e) =>
                onChange({ ...form, validation: { ...form.validation, required: e.target.checked } })
              }
              className="h-4 w-4 text-primary-600 rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-200">Required</span>
          </label>
          {(form.type === 'text' || form.type === 'textarea') && (
            <>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">Min:</span>
                <input
                  type="number"
                  value={form.validation.minLength || ''}
                  onChange={(e) =>
                    onChange({
                      ...form,
                      validation: {
                        ...form.validation,
                        minLength: e.target.value ? parseInt(e.target.value) : undefined,
                      },
                    })
                  }
                  className="w-16 px-2 py-1 text-sm border border-gray-300 rounded text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">Max:</span>
                <input
                  type="number"
                  value={form.validation.maxLength || ''}
                  onChange={(e) =>
                    onChange({
                      ...form,
                      validation: {
                        ...form.validation,
                        maxLength: e.target.value ? parseInt(e.target.value) : undefined,
                      },
                    })
                  }
                  className="w-16 px-2 py-1 text-sm border border-gray-300 rounded text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                />
              </div>
            </>
          )}
          {form.type === 'number' && (
            <>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">Min:</span>
                <input
                  type="number"
                  value={form.validation.min ?? ''}
                  onChange={(e) =>
                    onChange({
                      ...form,
                      validation: {
                        ...form.validation,
                        min: e.target.value ? parseFloat(e.target.value) : undefined,
                      },
                    })
                  }
                  className="w-20 px-2 py-1 text-sm border border-gray-300 rounded text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">Max:</span>
                <input
                  type="number"
                  value={form.validation.max ?? ''}
                  onChange={(e) =>
                    onChange({
                      ...form,
                      validation: {
                        ...form.validation,
                        max: e.target.value ? parseFloat(e.target.value) : undefined,
                      },
                    })
                  }
                  className="w-20 px-2 py-1 text-sm border border-gray-300 rounded text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                />
              </div>
            </>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={onSave} disabled={!form.name.trim() || !form.key.trim()}>
          <Check className="h-4 w-4 mr-1" />
          {isEditing ? 'Update' : 'Add'} Field
        </Button>
      </div>
    </div>
  );
}
